import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { Subscription, map, startWith } from 'rxjs';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatChipInputEvent,
  MatChipsModule,
  MatFormFieldModule,
  MatIconModule,
} from 'src/app/shared/material';
import { DynamicFormControl } from '../../models/dynamic-form-control.model';
import { FilterService } from 'src/app/shared/filtering/services/filter.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-multi-select-control',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  templateUrl: './multi-select-control.component.html',
  styleUrl: './multi-select-control.component.scss',
})
export class MultiSelectControlComponent {
  @Input() control: DynamicFormControl;
  @ViewChild('autoInput', { static: true })
  autoInput: ElementRef<HTMLInputElement>;
  autoCompleteControl: FormControl;
  selectedValuesFormControl: FormControl;
  selectedValues: { value: any; label: string }[] = [];
  separatorKeysCodes = [COMMA, ENTER];
  isTextInputEnabled: boolean;

  private _subscriptions: Subscription[] = [];

  constructor(private filterService: FilterService) {}

  ngOnInit() {
    this.autoCompleteControl = this.control.formControls.find((i) =>
      i.propertyBinding.includes('autocomplete'),
    ).formControl;

    this.selectedValuesFormControl = this.control.formControls.find(
      (i) => !i.propertyBinding.includes('autocomplete'),
    ).formControl;

    let sub = this.autoCompleteControl.valueChanges
      .pipe(
        startWith(null),
        map((value: string | null) => {
          const searchValue = typeof value === 'string' ? value : null;

          if (searchValue) {
            this.control.customOptions['filteredOptions'] =
              this.control.customOptions['selectOptions'].filter(
                (i: any) =>
                  !this.isOptionSelected(i) &&
                  i.label?.toLowerCase().includes(searchValue.toLowerCase()),
              );
          } else {
            this.control.customOptions['filteredOptions'] =
              this.control.customOptions['selectOptions'].filter(
                (i: any) => !this.isOptionSelected(i),
              );
          }
        }),
      )
      .subscribe();
    this._subscriptions.push(sub);

    sub = this.filterService.activeFilterRemoved$.subscribe({
      next: (i) => {
        if (i.propertyBinding === this.control.propertyBinding) {
          this.remove({ value: i.propertyValue, label: i.value });
          this.autoCompleteControl.setValue(null);
        }
      },
      error: (e) => console.log(e),
    });
    this._subscriptions.push(sub);

    //listening for external form control changes (from filter drawer) and clearing events
    sub = this.selectedValuesFormControl.valueChanges
      .pipe(startWith(this.selectedValuesFormControl.value))
      .subscribe((i) => {
        if (i === null || i === undefined) {
          // Clear everything
          this.selectedValues = [];
          this.autoCompleteControl.setValue(null);
          if (this.autoInput?.nativeElement) {
            this.autoInput.nativeElement.value = '';
          }
          // Refresh options when clearing
          this.refreshFilteredOptions();
        } else if (Array.isArray(i)) {
          // Check if this is an external update by comparing with current selectedValues
          const currentLabels = this.selectedValues.map((item) => item.label);
          const isExternalUpdate = !this.arraysEqual(currentLabels, i);

          if (isExternalUpdate) {
            // External update (from filter drawer) - sync selectedValues with form control
            this.selectedValues = i
              .map((item: string) => {
                const found = this.control.customOptions['selectOptions'].find(
                  (x: any) => x.label === item,
                );
                return found;
              })
              .filter(Boolean);

            // Only refresh when it's truly an external update
            this.refreshFilteredOptions();
          }
        }
      });
    this._subscriptions.push(sub);
    this.isTextInputEnabled =
      this.control.customOptions?.textInputEnabled !== false;
  }

  ngOnDestroy() {
    this._subscriptions.forEach((i) => i.unsubscribe());
  }

  remove(item: { value: any; label: string }) {
    let chipValue = this.selectedValues.find(
      (i) => i.value === item.value && i.label === item.label,
    );
    let index = this.selectedValues.indexOf(chipValue);
    if (index >= 0) {
      this.selectedValues.splice(index, 1);

      this.selectedValuesFormControl.setValue(
        this.selectedValues.map((i) => i.label),
      );

      this.autoCompleteControl.setValue(null);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const isAlreadySelected = this.selectedValues.some(
      (selected) =>
        selected.value === event.option.value.value &&
        selected.label === event.option.value.label,
    );

    if (!isAlreadySelected) {
      this.selectedValues.push(event.option.value);
      this.selectedValuesFormControl.setValue(
        this.selectedValues.map((i) => i.label),
      );
    }

    this.autoCompleteControl.setValue(null);

    if (this.autoInput?.nativeElement) {
      this.autoInput.nativeElement.value = '';
    }
  }

  add(event: MatChipInputEvent): void {
    const value = event.value;
    const input = event.chipInput.inputElement;

    if ((value || '').trim()) {
      var option = this.control.customOptions.selectOptions.find((i: any) =>
        i.label?.toLowerCase().includes(value.toLowerCase()),
      );

      if (option) {
        const isAlreadySelected = this.selectedValues.some(
          (selected) =>
            selected.value === option.value && selected.label === option.label,
        );

        if (!isAlreadySelected) {
          this.selectedValues.push(option);
          this.selectedValuesFormControl.setValue(
            this.selectedValues.map((i) => i.label),
          );
        }
      }
    }

    if (input) {
      input.value = '';
    }
    this.autoCompleteControl.setValue(null);
  }

  private arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }

  private isOptionSelected(option: any): boolean {
    return this.selectedValues.some(
      (selected) =>
        selected.value === option.value && selected.label === option.label,
    );
  }

  private refreshFilteredOptions(): void {
    const currentValue = this.autoCompleteControl.value;
    const searchValue = typeof currentValue === 'string' ? currentValue : null;

    if (searchValue) {
      this.control.customOptions['filteredOptions'] =
        this.control.customOptions['selectOptions'].filter(
          (i: any) =>
            !this.isOptionSelected(i) &&
            i.label?.toLowerCase().includes(searchValue.toLowerCase()),
        );
    } else {
      this.control.customOptions['filteredOptions'] =
        this.control.customOptions['selectOptions'].filter(
          (i: any) => !this.isOptionSelected(i),
        );
    }
  }

  public preventTextInput(event: KeyboardEvent) {
    if (this.control.customOptions?.textInputEnabled === false) {
      event.preventDefault();
    }
  }

  public preventPasteInput(event: Event) {
    if (this.control.customOptions?.textInputEnabled === false) {
      event.preventDefault();
    }
  }

  public openSelectDropdown() {
    this.autoInput.nativeElement.click();
  }
}
